import { Button, Input, Radio, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useState } from "react";
import { Dialog as ZhDialog } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { useUserStore } from "@/views/stores";
import RequiredMark from "@/views/components/RequiredMark";
import { confirmDialog } from "@/utils/confirmDialog";
import { TRANSPORT_CAPACITY_STATE } from "@zhihaoscm/meta";
import type { TransportCapacityRow } from "./TransportCapacityManagementPage";

export interface TransportCapacityReviewProps {
    capacity: TransportCapacityRow;
    validateAudit?: (id: string) => Promise<{ resultType?: number }>;
    auditCapacity?: (id: string, payload: { state: number; rejectReason?: string }) => Promise<unknown>;
    canManage?: boolean;
}
/** 运力审核组件：先执行 Vue 审核校验，校验失败时禁止发送审核写请求。 */
export async function validateTransportCapacityAudit(id: string) {
    const response = await shippingRequestHttp.service.get(`/owner-ship/find/review/validate/${id}`);
    return response.data as { resultType?: number };
}
export async function auditTransportCapacity(id: string, payload: { state: number; rejectReason?: string }) {
    const response = await shippingRequestHttp.service.put(`/owner-ship/review/${id}`, payload);
    return response.data;
}
export function TransportCapacityReview({
    capacity,
    validateAudit = validateTransportCapacityAudit,
    auditCapacity = auditTransportCapacity,
    canManage
}: TransportCapacityReviewProps) {
    const storeCanManage = useUserStore(state => state.hasAnyPermission(["ROLE_CM_M"]));
    const mayManage = canManage ?? storeCanManage;
    const [open, setOpen] = useState(false);
    const [resultType, setResultType] = useState<number>();
    const [auditResult, setAuditResult] = useState<1 | 2>();
    const [rejectReason, setRejectReason] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const check = async () => {
        if (!capacity.ownerShip.id) return;
        setErrorMessage("");
        try {
            const result = await validateAudit(capacity.ownerShip.id);
            setResultType(result.resultType);
            setOpen(true);
        } catch {
            setErrorMessage("审核校验失败");
        }
    };
    const confirm = async () => {
        if (!capacity.ownerShip.id || !auditResult) return;
        if (auditResult === 2 && !rejectReason.trim()) {
            setErrorMessage("请输入驳回原因");
            return;
        }
        // 业务审核弹窗仅收集审核意见；写请求前继续保留 Vue 的最终二次确认。
        const confirmMessage = auditResult === 1 ? "确认审核通过吗？" : "确认审核驳回吗？";
        if (!(await confirmDialog(confirmMessage, "确认"))) return;
        setLoading(true);
        try {
            await auditCapacity(capacity.ownerShip.id, {
                state:
                    auditResult === 1 ? TRANSPORT_CAPACITY_STATE.APPROVED.code : TRANSPORT_CAPACITY_STATE.REJECTED.code,
                ...(auditResult === 2 ? { rejectReason: rejectReason.trim() } : {})
            });
            setOpen(false);
        } catch {
            setErrorMessage("审核失败");
        } finally {
            setLoading(false);
        }
    };
    const blocked = [3, 4, 6, 9, 10].includes(Number(resultType));
    return mayManage ? (
        <>
            <Button type="link" onClick={() => void check()}>
                审核
            </Button>
            {errorMessage && !open ? <Alert role="alert" type="error" message={errorMessage} /> : null}
            <ZhDialog
                open={open}
                title="运力审核"
                width={520}
                onCancel={() => setOpen(false)}
                footer={
                    blocked ? null : (
                        <Space>
                            <Button disabled={loading} onClick={() => setOpen(false)}>
                                取消
                            </Button>
                            <Button loading={loading} type="primary" onClick={() => void confirm()}>
                                确认审核
                            </Button>
                        </Space>
                    )
                }
            >
                {blocked ? (
                    <Alert
                        message={
                            resultType === 3 || resultType === 9
                                ? "此船舶货主已认证，请先查看详情"
                                : "当前运力存在关联数据，请查看详情"
                        }
                        type="warning"
                    />
                ) : (
                    <Space direction="vertical">
                        <Radio.Group
                            aria-label="审核意见"
                            value={auditResult}
                            onChange={event => setAuditResult(event.target.value)}
                        >
                            <Radio value={1} disabled={resultType === 9}>
                                同意
                            </Radio>
                            <Radio value={2}>驳回</Radio>
                        </Radio.Group>
                        {auditResult === 2 ? (
                            <label>
                                <span>
                                    <RequiredMark />
                                    驳回原因
                                </span>
                                <Input.TextArea
                                    aria-label="驳回原因"
                                    maxLength={50}
                                    value={rejectReason}
                                    onChange={event => setRejectReason(event.target.value)}
                                    placeholder="请输入50字以内的拒绝原因"
                                />
                            </label>
                        ) : null}
                    </Space>
                )}
            </ZhDialog>
        </>
    ) : null;
}

export default TransportCapacityReview;
